import { X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';

import { Dialog, DialogClose, DialogContent, DialogTitle } from '@/components/ui/dialog';

import { CustomFilterContent } from './CustomFilterContent';
import { FilterModeSelector } from './FilterModeSelector';
import { FolderFilterContent } from './FolderFilterContent';
import { useEmailContextDialogState } from './useEmailContextDialogState';

interface EmailContextDialogProps {
  activeSessionContext?:
    | {
        context?: string;
        conversationId?: string;
        emailId?: string;
      }
    | undefined;
  contextEmailIds?: Array<string> | undefined;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  sessionId?: string | null;
}

export function EmailContextDialog({
  activeSessionContext,
  contextEmailIds,
  onOpenChange,
  open = false,
  sessionId,
}: EmailContextDialogProps) {
  const { t } = useTranslation();

  const {
    canUseCurrentEmail,
    canUseCurrentThread,
    composeMode,
    counterHighlight,
    debouncedSearch,
    emails,
    error,
    filterMode,
    handleFilterModeChange,
    handleOpenChange,
    handleSelectFolder,
    hasNextPage,
    inboxScrollRef,
    inboxSentinelRef,
    isFetchingNextPage,
    isLoading,
    ragSearchEnabled,
    resultCount,
    searchQuery,
    selectedCount,
    selectedEmailItems,
    selectedEmails,
    selectedEmailsSectionRef,
    selectedFolderId,
    setSearchQuery,
    showCheckboxes,
    showSyncingState,
    toggleEmail,
  } = useEmailContextDialogState({
    activeSessionContext,
    contextEmailIds,
    onOpenChange,
    open,
    sessionId,
  });

  const dropdownContent = (
    <div className="border-border-subtle bg-popover flex w-full flex-col overflow-hidden rounded-md border-[0.5px] shadow-[5px_5px_30px_5px_#71778B0D,0px_1px_4px_0px_#71778B40]">
      <div className="border-border-subtle flex shrink-0 items-center justify-between border-b px-3 py-2">
        <DialogTitle className="text-foreground font-['Inter'] text-[12px] leading-[170%] font-semibold">
          {t('assistant.emailContext.title')}
        </DialogTitle>
        <DialogClose className="text-tertiary hover:text-foreground flex h-5 w-5 cursor-pointer items-center justify-center rounded-sm transition-colors">
          <X className="h-3.5 w-3.5" />
        </DialogClose>
      </div>
      <motion.div
        className="custom-scrollbar min-h-0 flex-1 overflow-y-auto"
        layoutScroll
        ref={inboxScrollRef}
      >
        <FilterModeSelector
          filterMode={filterMode}
          isComposeMode={composeMode}
          onFilterModeChange={handleFilterModeChange}
          options={{
            canUseAllMails: ragSearchEnabled && !showSyncingState,
            canUseCurrentEmail,
            canUseCurrentThread,
            ragSearchEnabled,
            showSyncingState,
          }}
        />

        <AnimatePresence initial={false}>
          {filterMode === 'custom' && (
            <motion.div
              animate={{ height: 'auto', opacity: 1 }}
              className="overflow-hidden"
              exit={{ height: 0, opacity: 0 }}
              initial={{ height: 0, opacity: 0 }}
              key="custom"
              transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
            >
              <CustomFilterContent
                counterHighlight={counterHighlight}
                debouncedSearch={debouncedSearch}
                emails={emails}
                error={error}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                isLoading={isLoading}
                onSearchChange={setSearchQuery}
                onToggleEmail={toggleEmail}
                resultCount={resultCount}
                searchQuery={searchQuery}
                selectedCount={selectedCount}
                selectedEmailIds={selectedEmails}
                selectedEmailItems={selectedEmailItems}
                selectedEmailsSectionRef={selectedEmailsSectionRef}
                sentinelRef={inboxSentinelRef}
                showCheckboxes={showCheckboxes}
              />
            </motion.div>
          )}
          {filterMode === 'folder' && (
            <motion.div
              animate={{ height: 'auto', opacity: 1 }}
              className="overflow-hidden"
              exit={{ height: 0, opacity: 0 }}
              initial={{ height: 0, opacity: 0 }}
              key="folder"
              transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
            >
              <FolderFilterContent
                onSelectFolder={handleSelectFolder}
                selectedFolderId={selectedFolderId}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogContent
        className="flex max-h-[90vh] w-full max-w-[min(28rem,calc(100vw-32px))] flex-col overflow-hidden rounded-md border-0 bg-transparent p-0 shadow-none sm:max-w-[min(28rem,calc(100vw-32px))]"
        showCloseButton={false}
      >
        <div className="flex w-full flex-col overflow-hidden px-3 py-2">{dropdownContent}</div>
      </DialogContent>
    </Dialog>
  );
}
